<script lang="ts">
	/**
	 * `/Checkout/Start` — the far side of the sign-in detour (Design §14).
	 *
	 * The customer arrives here either straight from Buy or back from AbstractID, with nothing in
	 * the URL but the intent nonce. This page resolves the nonce, creates the session and leaves for
	 * the provider; it renders only while that is in flight, when it needs one more answer, or when
	 * it could not be done.
	 *
	 * It is deliberately **not** in `AbstractID.publicRoutes`, and that is not a hole in "buy
	 * without an account" (§14) but the shape of it: the sign-in happens *before* this page, in
	 * the product page's inline checkout, where the customer can still see what they are buying. By
	 * anyone is routed here there is a token, so an arrival without one is a stale link or a typed
	 * URL — for which the inherited redirect is the correct guard, and this page has nothing to
	 * render anyway since every request it makes is `requireAuth`.
	 *
	 * An email field lived here briefly and was moved. Asked on this page it was asked on a screen
	 * that no longer showed the plan, the price or the discount the customer had just agreed to,
	 * which reads like a newsletter box rather than the next step of a purchase.
	 *
	 * **The terms are ticked on the product page, not here** (§4.7). They are part of the decision
	 * to buy rather than a step after it. The ticks arrive in the intent and are sent with the
	 * session; the server refuses a purchase whose terms are missing or stale regardless.
	 *
	 * **The country is asked in the confirmation window now, not here** (§10.1). A store that works
	 * out its own tax says so on `/api/configuration`, per caller, so the window can put the field
	 * beside the plan and the price it is about. The prompt below is the **fallback**: it covers an
	 * intent written before the customer's country was known, and a country the store turns out not
	 * to be able to price into — so an arrival here is never a dead end over a question somebody is
	 * perfectly willing to answer.
	 */

	import { onMount } from 'svelte';
	import { Button, Input } from 'flowbite-svelte';
	import {
		CountrySelect,
		EmptyState,
		FormField,
		PageHeader,
		PurchaseAccount,
		PurchaseSummary,
		RoutedButton
	} from '../../components';
	import { conditionOf, createCheckoutSession, getCatalogProduct } from '../../AppNetwork';
	import { activeCurrency } from '../../stores/storefront';
	import { configurationStore } from '../../AppConfiguration';
	import type { Configuration, Price, Product } from '../../AppTypes';
	import { readIntent } from '../../stores/checkoutIntent';
	import type { PurchaseIntent } from '../../stores/checkoutIntent';
	import AbstractID from '../../AbstractID';
	import { flush, sessionID, setAnalyticsContext, track, visitorID } from '../../stores/analytics';

	/** `working` covers the redirect too — the page never resolves to a success state of its own. */
	let phase: 'working' | 'lost' | 'country' | 'failed' = 'working';
	let failureMessage: string = '';
	let productSlug: string = '';

	/**
	 * The intent, kept after `readIntent` consumed it.
	 *
	 * `readIntent` reads *and deletes* (§14), so the country answer has to retry against this copy
	 * rather than the nonce — going back to `sessionStorage` would find nothing and report the
	 * purchase lost, one keystroke after the customer told us how to complete it.
	 */
	let intent: PurchaseIntent | null = null;
	/**
	 * §10.1's answer, given in the confirmation window and carried here in the intent.
	 *
	 * Empty is the ordinary case — every store on the provider's own tax engine, and every purchase
	 * after the first — and an empty value sends no field at all.
	 */
	let country: string = '';
	/**
	 * The company, when the buyer is one. Optional, so it never blocks the form.
	 *
	 * Asked beside the country because it is the other half of the same question (§10.1): the
	 * country picks the jurisdiction and this picks which of its two rates applies. Prefilled from
	 * the intent, since the confirmation window asks first and this page is the fallback.
	 */
	let company: string = '';
	let countryError: string = '';

	/**
	 * Filled in behind the intent from what the store already holds for this customer (§10.1).
	 *
	 * This page asks for a country in two situations, and only the second has anything to prefill.
	 * `customer.countryRequired` is a first purchase, where by definition nothing is on file. A
	 * **`tax.*` refusal** is the other: the country we hold is one the store is liable in with no
	 * rate on file, or one whose rate an admin switched off — the field comes back so a country
	 * that cannot be priced can be corrected rather than costing the purchase, and rendering it
	 * empty there makes the customer guess which country the store thinks they are in. That is the
	 * one fact this screen is in a position to tell them.
	 *
	 * Behind the intent, never over it: what the customer chose in the confirmation window is a
	 * later answer than anything on file, and overwriting it would silently discard the correction
	 * they came here to make. Once only, so a configuration reload cannot refill a field they
	 * deliberately cleared.
	 */
	let identitySeeded: boolean = false;

	function fnSeedBillingIdentity(config: Configuration | null): void {
		// `intent === null` before `onMount` has read it, and seeding then would be overwritten by
		// the intent's own empty values a moment later — which is the same as not seeding at all.
		if (config === null || intent === null || identitySeeded) return;

		// Nothing to offer is not a seeding: an anonymous read carries null for both, and marking
		// it seeded would spend the one shot on the read that knows nothing about the buyer.
		const held = config.billingCountry ?? '';
		const heldCompany = config.billingCompany ?? '';
		if (held.length === 0 && heldCompany.length === 0) return;

		identitySeeded = true;
		if (country.length === 0) country = held;
		if (company.length === 0) company = heldCompany;
	}

	/*
	 * Depends on the store alone, so it fires when a configuration read lands after this page is
	 * already up. `onMount` calls the same function directly for the ordinary case, where the
	 * configuration was loaded at boot and this statement has already run and bailed on a null
	 * intent.
	 */
	$: fnSeedBillingIdentity($configurationStore);

	/**
	 * What the customer is buying, re-read here so it can be shown beside the questions.
	 *
	 * The intent carries identifiers, not a product — it was written to be small and to survive a
	 * detour, and it names the price rather than describing it. So the page fetches the product
	 * again from the **public** catalogue read, which is why this works before anyone has signed
	 * in: `getCatalogProduct` goes through `optionalAuthFetch` and serves an anonymous caller.
	 *
	 * Its failure is **swallowed**, exactly as the product page swallows its tier strip's: a
	 * customer who has confirmed a purchase must not be shown an error state — still less be
	 * unable to complete it — because a card describing what they already chose could not be
	 * drawn. The summary simply does not render, and the purchase carries on.
	 */
	let product: Product | null = null;
	let price: Price | null = null;

	/** Drawn on the one phase that stops and asks something; the rest are in flight or dead ends. */
	$: showSummary = product !== null && price !== null && phase === 'country';

	async function fnLoadSummary(): Promise<void> {
		if (intent === null) return;

		try {
			const fetched = await getCatalogProduct(intent.productSlug, {
				// The currency the customer was quoted in, so the figure here is the figure they saw.
				...(intent.currency.length > 0 ? { currency: intent.currency } : {})
			});

			// Only once both are in hand: a product whose price is not among its own is a mismatch
			// between the intent and the catalogue, and half a summary is worse than none.
			const matched = fetched.prices.find(candidate => candidate.uuid === intent?.priceUUID) ?? null;
			if (matched === null) return;

			product = fetched;
			price = matched;
		} catch {
			/* See above — the purchase is what matters, not the picture of it. */
		}
	}

	/** Back to the product where the choice was made, when the intent told us which one. */
	$: backHref = productSlug.length > 0 ? `/Product/${productSlug}` : '/';

	/**
	 * Creates the session and leaves, or decides which of this page's three stopping points to show.
	 *
	 * `billingCountry` is sent only once there is one to send, so a store on the provider's own tax
	 * engine — every store by default — never produces the question at all.
	 */
	async function fnCreate(): Promise<void> {
		if (intent === null) {
			phase = 'lost';
			return;
		}

		phase = 'working';

		try {
			// The server validates this body strictly, so an absent coupon or currency is **omitted**
			// rather than sent as null or undefined.
			const session = await createCheckoutSession({
				priceUUID: intent.priceUUID,
				...(intent.couponCode !== null ? { couponCode: intent.couponCode } : {}),
				...(intent.currency.length > 0 ? { currency: intent.currency } : {}),
				// Absent means "apply the price's floor", which is not the same as one seat — so an
				// intent that carries no count omits the field rather than sending a number.
				...(intent.seats !== null ? { seats: intent.seats } : {}),
				// §4.7 — ticked on the product page and carried here with everything else. The
				// version travels with each one, so wording that changed while the customer was
				// reading is refused by the server rather than silently treated as agreed.
				...(intent.acceptedClickwraps.length > 0
					? { acceptedClickwraps: intent.acceptedClickwraps }
					: {}),
				// §4.8 — the options ticked on the product page, carried the same way and for the
				// same reason. Omitted when none were taken, which is the ordinary purchase: an
				// option is offered, so an empty list and a missing field mean the same thing.
				...(intent.selectedOptions.length > 0
					? { selectedOptions: intent.selectedOptions }
					: {}),
				...(country.length > 0 ? { billingCountry: country } : {}),
				// Omitted when blank rather than sent empty: to the server an absent company is a
				// buyer who is not one, and a blank string would be a second spelling of it.
				...(company.trim().length > 0 ? { billingCompany: company.trim() } : {}),
				// §10.1 — only where the product page offered the box; null sends nothing.
				...(typeof intent.billingProfessional === 'boolean' ? { billingProfessional: intent.billingProfessional } : {}),
				/*
				 * §12.4 — the journey this purchase came out of, snapshotted onto the checkout
				 * session so the sale can still name its campaign years later, long after the
				 * visitor's events have been pruned. Opaque grouping tokens: they grant nothing,
				 * and identity on this call still comes from the token.
				 */
				visitorUUID: visitorID(),
				analyticsSessionUUID: sessionID()
			});

			track('checkout_created', {
				productSlug,
				priceUUID: intent.priceUUID,
				currency: intent.currency,
				properties: {
					seats: intent.seats ?? 1,
					coupon: intent.couponCode !== null,
					options: intent.selectedOptions.length
				}
			});
			// Flushed rather than queued: the very next statement leaves the origin, and a batch
			// still waiting on its timer would go with the page.
			void flush(true);

			// Cross-origin, so it goes through the one method the app routes every such navigation
			// through — which is also what makes this hand-off observable from a test.
			AbstractID.navigateTo(session.url);
		} catch (error) {
			const message = (error as Error).message;
			const condition = conditionOf(error);

			/*
			 * Two refusals are questions rather than faults, and both belong on the country form.
			 *
			 * `customer.countryRequired` is the first ask. `tax.*` is the answer being one the store
			 * cannot price in — a country it is liable in with no rate on file, or one whose rate an
			 * admin switched off. Sending that to the dead end would make a mistyped country cost
			 * the customer the whole purchase, when the fix is one field away and already on screen.
			 */
			/*
			 * Recorded before it is branched on. `error_shown` carries the **condition**, never the
			 * prose: the condition is stable and can be grouped by, and the wording is free to
			 * change — which is the same reason `conditionOf` exists at all. This is what turns "83
			 * people abandoned" into "31 of them met `tax.countryMissing`".
			 */
			track('error_shown', {
				path: '/Checkout/Start',
				productSlug,
				properties: { condition: condition.length > 0 ? condition : 'unknown' }
			});

			if (condition === 'customer.countryRequired' || condition.startsWith('tax.')) {
				countryError = condition === 'customer.countryRequired' ? '' : message;
				phase = 'country';
				return;
			}

			// A 409 lands here, and its message is the whole content: "you already own this" and
			// "your account is billed in EUR" are answers the customer can act on.
			failureMessage = message;
			phase = 'failed';
		}
	}

	function fnSubmitCountry(): void {
		if (country.length === 0) {
			countryError = 'Please choose the country you are buying from.';
			return;
		}
		countryError = '';
		void fnCreate();
	}

	onMount(() => {
		const nonce = new URLSearchParams(window.location.search).get('intent') ?? '';
		intent = readIntent(nonce);

		if (intent === null) {
			phase = 'lost';
			return;
		}

		/*
		 * §12.2 — what this hand-off is about, taken from the intent since the page has no product
		 * of its own. It is what attributes a `tax.*` refusal raised here to the product it cost,
		 * rather than to nothing.
		 */
		setAnalyticsContext({
			productSlug: intent.productSlug,
			priceUUID: intent.priceUUID,
			...(intent.currency.length > 0 ? { currency: intent.currency } : {})
		});

		productSlug = intent.productSlug;
		// Whatever the confirmation window already collected. Empty sends nothing — and empty is
		// then filled in from what the store already holds, which is the `tax.*` case above.
		country = intent.billingCountry;
		company = intent.billingCompany;
		fnSeedBillingIdentity($configurationStore);

		/*
		 * Started together, not one after the other.
		 *
		 * The summary is decoration for the common path — the page redirects before anything is
		 * drawn — but on the path where it stops to ask something it has to be *already there*.
		 * Fetching it only on reaching the question puts a blank card beside the field for a round
		 * trip, on the screen where the customer most needs to see what they are paying for.
		 */
		void fnLoadSummary();
		void fnCreate();
	});
</script>

<PageHeader title="Starting checkout" />

{#if phase === 'working'}
	<!--
		Whose subscription this will be (§14), on the way out as well as at the question. A customer
		signed in as the wrong person needs to find that out before the money moves, and this is the
		last screen under our control before it does.
	-->
	<div class="max-w-md mx-auto space-y-4 text-center">
		<EmptyState variant="loading" title="Taking you to the payment page…" />
		<PurchaseAccount />
	</div>
{:else if phase === 'lost'}
	<EmptyState
		variant="error"
		faIcon="hourglass-end"
		title="That purchase could not be resumed."
		description="The details of what you were buying are held only for a short while, and this link has already been used or has expired. Pick the plan again and nothing will be lost."
	>
		<RoutedButton href={backHref} color="primary" size="sm">Back to the product</RoutedButton>
	</EmptyState>
{:else if phase === 'country'}
	<div class="max-w-md mx-auto space-y-4">
		{#if showSummary}
			<!--
				What is being bought, beside the question. A page that stops to ask something with no
				mention of what it is for reads like a form that appeared for no reason.
			-->
			<PurchaseSummary
				{product}
				{price}
				currency={$activeCurrency}
				seats={intent?.seats ?? null}
				couponCode={intent?.couponCode ?? null}
			/>
		{/if}

		<div class="widget space-y-4">
			<div>
				<h2 class="widget-title flex items-center gap-2">
					<i class="fa-solid fa-earth-europe opacity-70" />Where are you buying from?
				</h2>
				<p class="text-sm text-default mt-1">
					Your country decides the tax on this purchase, so we need it before we can take you to
					the payment page. You'll enter the rest of your billing address there.
				</p>
			</div>

			<!-- Whose subscription this will be. By this point there is always an account. -->
			<PurchaseAccount />

			<form class="space-y-4" on:submit|preventDefault={fnSubmitCountry}>
				<FormField label="Country" id="checkout-country" required error={countryError}>
					<CountrySelect id="checkout-country" bind:value={country} required />
				</FormField>

				<!--
					Optional, and labelled so. Naming a company makes this a business purchase: the
					invoice is billed to the company and the jurisdiction's business rate applies,
					neither of which a later edit can apply retrospectively to a document already
					issued.
				-->
				<FormField
					label="Company (optional)"
					id="checkout-company"
					description="If you're buying for a company, we'll bill the company and apply business tax rates."
				>
					<Input
						id="checkout-company"
						bind:value={company}
						placeholder="Company name"
						autocomplete="organization"
					/>
				</FormField>

				<div class="flex flex-wrap items-center gap-3">
					<Button type="submit" color="primary">
						<i class="fa-solid fa-arrow-right-to-bracket mr-2" />Continue to payment
					</Button>
					<RoutedButton href={backHref} color="alternative" size="sm">Cancel</RoutedButton>
				</div>
			</form>
		</div>
	</div>
{:else}
	<EmptyState variant="error" title="Checkout could not be started." description={failureMessage}>
		<RoutedButton href={backHref} color="primary" size="sm">Back to the product</RoutedButton>
	</EmptyState>
{/if}
